import { axiosSubscriptionInstance } from "../configs/AxiosInstance";
import { buildQuery } from "./comms/_query";
import { wrapCommsError } from "./comms/_error";

/**
 * Call Booking API — coach shifts, call types, generated slots and bookings.
 *
 * These routes live in microservice-subscriptions (booking_router.py, mounted
 * with prefix=""), the same FastAPI app that already serves /get-current-employee
 * and /get-common-utils — hence `axiosSubscriptionInstance` rather than a new one.
 *
 * Verbs are passed in as the first argument (the `employeeService` idiom) so this
 * module never imports the transport and stays trivially mockable. `buildQuery`
 * and `wrapCommsError` live under `service/comms/` but are backend-agnostic:
 * the former skips null/undefined params, the latter unwraps FastAPI's `detail`
 * and keeps `status` on the thrown Error — exactly what these routes return.
 */

/**
 * Bookings made against coach slots, in a date window.
 *
 * Scope, and why this is called *without* an id filter: the endpoint applies a
 * mandatory predicate of its own —
 *
 *   scope_bypass OR br.user_id = <caller> OR cs.employee_id = ANY(<caller's employee rows>)
 *
 * — where the employee list is *every* EmployeeSchema.Employees row belonging to
 * the caller's token. One unfiltered call therefore returns both the bookings on
 * this coach's own slots and the ones sitting on their other employee profile,
 * which is exactly what the dashboard and the slot-conflict check need between
 * them. Narrowing with `employeeId` would filter out the second set, so the
 * caller's own dashboard leaves it unset.
 *
 * The one caller that does pass `employeeId` is a manager reading a single
 * coach's month. That works because the predicate's employee list is
 * `actionable_employee_ids` — owned **and** direct reports — the same
 * reporting line `/modify-coach-shift` grants shift edits through, and the one
 * `modify-booking` checks a status correction against.
 *
 * `userId` is best left unset either way: an omitted `user_id` is *no filter*
 * rather than "me", and passing somebody else's is an outright 403 rather than
 * the silent narrowing `employee_id` gets. Both params stay in the signature
 * for callers that genuinely want one booking's worth of rows.
 *
 * Rows are **one per booking**, each with its *current* status (the newest
 * `BookingStatusHistory` row), so `limit` counts calls. Fields: `coach_id`,
 * `start_time`/`end_time` (the booking's own fixed time), `department`,
 * `shift_call_type_id`, `duration_minutes`. With `includeHistory` each record
 * also carries `status_history[]`, oldest first — the activity trail.
 *
 * Timezone caveat: these bounds are real timestamps (compared against
 * `start_time`), not date-only strings resolved against UTC midnight. So pass
 * offset-bearing ISO instants — `monthRange` in slotGrid — and no widening is
 * needed.
 *
 * @param {Function} get - The `get` verb from ApiContext.
 * @param {Object} params
 * @param {string} params.eventTypeId - Required by the API.
 * @param {string} [params.userId] - UserProfile id. See above: leave unset.
 * @param {string} [params.employeeId] - Employees id. See above: unset for the
 *   caller's own month, set to read one coach's.
 * @param {string} [params.bookingStatusId] - StatusUtils id, if one is ever known.
 * @param {string} [params.fromDate] - ISO instant, e.g. `2026-09-01T00:00:00.000Z`.
 * @param {string} [params.toDate] - ISO instant, inclusive.
 * @param {number} [params.limit] - Server default 200, hard max 500.
 * @param {boolean} [params.includeHistory] - Also return each booking's `status_history`.
 * @returns {Promise<{ booking_records: Array<Object>, total_count: number, scope?: Object }>}
 */
export const fetchBookingRecords = async (
  get,
  {
    eventTypeId,
    userId,
    employeeId,
    bookingStatusId,
    fromDate,
    toDate,
    limit,
    includeHistory,
  } = {}
) => {
  try {
    const query = buildQuery({
      event_type_id: eventTypeId,
      user_id: userId,
      employee_id: employeeId,
      booking_status_id: bookingStatusId,
      from_date: fromDate,
      to_date: toDate,
      limit,
      include_history: includeHistory ? true : undefined,
    });
    const response = await get(`/fetch-booking-records${query}`, {
      instance: axiosSubscriptionInstance,
    });
    return response?.data || { booking_records: [], total_count: 0 };
  } catch (err) {
    throw wrapCommsError(err, "Failed to load bookings");
  }
};

/**
 * A coach's weekly shifts — when they work, per weekday, repeating every week.
 *
 * Token-only: every authenticated caller may read every coach's shifts, which is
 * what lets a coach see their own read-only. Times are **UTC wall-clock**
 * (`"09:00:00"`) on an ISO `day_number` (Mon=1 … Sun=7); convert with
 * `utcShiftsToLocal` in slotGrid before painting.
 *
 * Since `be3e423` each shift is **effective-dated**: `effective_from`
 * (inclusive) to `effective_to` (exclusive, null = carries forward), both UTC
 * instants. With no `startDate`/`endDate` every shift is returned, superseded
 * ones included — which is what the week table wants, so a past week still
 * shows the hours it had. `startDate` keeps shifts still in effect on/after it
 * (against `effective_to`); `endDate` is exclusive (against `effective_from`).
 *
 * @param {Function} get
 * @param {{ eventTypeId: string, employeeId?: string, weekdayId?: string, startDate?: string, endDate?: string }} params
 * @returns {Promise<{ coach_shifts: Array<Object>, total_count: number }>}
 */
export const fetchCoachShifts = async (
  get,
  { eventTypeId, employeeId, weekdayId, startDate, endDate } = {}
) => {
  try {
    const query = buildQuery({
      event_type_id: eventTypeId,
      employee_id: employeeId,
      weekday_id: weekdayId,
      start_date: startDate,
      end_date: endDate,
    });
    const response = await get(`/fetch-coach-shift${query}`, {
      instance: axiosSubscriptionInstance,
    });
    return response?.data || { coach_shifts: [], total_count: 0 };
  } catch (err) {
    throw wrapCommsError(err, "Failed to load shifts");
  }
};

/**
 * Create, update or delete coach shifts in one batch.
 *
 * The server processes **ADD, then UPDATE, then DELETE** in one transaction.
 * Since `be3e423` an ADD needs `effective_from` (an offset-bearing instant; a
 * naive one is a 422), and an ADD overlapping a stored open shift on the same
 * weekday and hours **supersedes** it — closes it at this `effective_from` —
 * rather than being refused. An UPDATE, or a supersession close, that would
 * leave an upcoming booked call outside its shift is a 409 listing those calls;
 * a DELETE is not checked — which is why removing a shift is always a close
 * (`planShiftRemoval` in slotGrid), never a DELETE.
 *
 * Authority: an executive for any calendar; otherwise a direct report's, or
 * the caller's own under a live `Manager`-titled role. Build items with
 * `buildShiftItems` so times go out as UTC `"HH:MM:SS"` and a shift crossing
 * UTC midnight is split into two.
 *
 * @param {Function} post
 * @param {{ eventTypeId: string, coachShifts: Array<Object> }} params
 * @returns {Promise<{ coach_shift_summary: Object, coach_shift_records: Array<Object> }>}
 */
export const modifyCoachShifts = async (post, { eventTypeId, coachShifts } = {}) => {
  try {
    const response = await post(
      "/modify-coach-shift",
      { event_type_id: eventTypeId, coach_shifts: coachShifts },
      { instance: axiosSubscriptionInstance }
    );
    return response?.data?.data || response?.data || {};
  } catch (err) {
    throw wrapCommsError(err, "Failed to save shifts");
  }
};

/**
 * The free slots one coach has for one member, and the call type that sizes
 * them — the member's call number picks it.
 *
 * Off-shift booking reads only the response-level call type
 * (`shift_call_type_id`, `call_no`, `duration_minutes`, `buffer_minutes`): an
 * ADD must send back the one that applies to this member now, or it is a 400.
 *
 * Dates are `YYYY-MM-DD` resolved against **UTC** midnight, `to_date`
 * exclusive — widen a local day by one either side (`slotDayWindow`).
 * `departmentId` settles a coach in two departments; send it only when the
 * calendar's department is known, or when a 409 asks for it.
 *
 * @param {Function} get
 * @param {{ eventTypeId: string, employeeId: string, coachShiftTypeId: string,
 *   userId?: string, departmentId?: string, fromDate: string, toDate: string }} params
 * @returns {Promise<{ coach_slots: Array<Object>, call_no: number, shift_call_type_id: string,
 *   duration_minutes: number, buffer_minutes: number }>}
 */
export const fetchCoachSlots = async (
  get,
  { eventTypeId, employeeId, coachShiftTypeId, userId, departmentId, fromDate, toDate } = {}
) => {
  try {
    const query = buildQuery({
      event_type_id: eventTypeId,
      employee_id: employeeId,
      coach_shift_type_id: coachShiftTypeId,
      user_id: userId,
      department_id: departmentId,
      from_date: fromDate,
      to_date: toDate,
    });
    const response = await get(`/fetch-coach-slot${query}`, {
      instance: axiosSubscriptionInstance,
    });
    return response?.data || {};
  } catch (err) {
    throw wrapCommsError(err, "Failed to load this client's call type");
  }
};

/**
 * Call types — how long the *N*th call with a department's coach lasts, and
 * the buffer after it. Token-only ("call types are not private").
 *
 * They belong to a **department**, not a coach. `call_no` is 0-based and the
 * highest configured number repeats for every later call. This app configures
 * department-wide tiers only, as many as a department sets, on Diet Utils /
 * Workout Utils (`CallTypesSection`; named by `callTierList` in slotGrid).
 *
 * Rows carry lengths **only as `duration_time`/`buffer_time` ("HH:MM:SS")**
 * since `7428c14`; read them with `callTypeLengthMinutes`. They also carry
 * `assigned_by_name` (who last set the row). Every filter the API takes is
 * exact (`plan_id` no longer folds in the department defaults); we send none
 * but `department_id`, because View slots needs every call type's buffer —
 * a booking may point at a plan-specific one.
 *
 * @param {Function} get
 * @param {{ eventTypeId: string, departmentId?: string }} params
 * @returns {Promise<{ shift_call_types: Array<Object>, total_count: number }>}
 */
export const fetchShiftCallTypes = async (get, { eventTypeId, departmentId } = {}) => {
  try {
    const query = buildQuery({ event_type_id: eventTypeId, department_id: departmentId });
    const response = await get(`/fetch-shift-call-type${query}`, {
      instance: axiosSubscriptionInstance,
    });
    return response?.data || { shift_call_types: [], total_count: 0 };
  } catch (err) {
    throw wrapCommsError(err, "Failed to load call types");
  }
};

/**
 * Create, update or delete call types in one batch.
 *
 * Authority is **department membership**, not a reporting line: a live
 * `Manager`-titled role in that department, or an executive; anyone else is a
 * 403. `(department, plan, call_no)` is unique among live rows, so redefining
 * an existing tier must be an UPDATE (`buildCallTypeItems` decides). The
 * server runs deletes, then updates, then adds.
 *
 * @param {Function} post
 * @param {{ eventTypeId: string, shiftCallTypes: Array<Object> }} params
 * @returns {Promise<{ shift_call_type_summary: Object, shift_call_type_records: Array<Object> }>}
 */
export const modifyShiftCallTypes = async (post, { eventTypeId, shiftCallTypes } = {}) => {
  try {
    const response = await post(
      "/modify-shift-call-type",
      { event_type_id: eventTypeId, shift_call_types: shiftCallTypes },
      { instance: axiosSubscriptionInstance }
    );
    return response?.data?.data || response?.data || {};
  } catch (err) {
    throw wrapCommsError(err, "Failed to save call types");
  }
};

/**
 * Book calls, or change a booking's status / feedback, in one batch.
 *
 * Two operations only — there is no DELETE; cancelling is an UPDATE to
 * Cancelled, which is also what frees the coach's time.
 *
 * - **ADD** books by echoing the fetched slot: `coach_id`, `coach_shift_id`,
 *   `shift_call_type_id` (the fetch's response-level one), `slot_start` and
 *   `slot_end`, all exactly as fetch-coach-slot returned them (+ optional
 *   `user_id`). The server regenerates the grid and refuses any mismatch. The
 *   booking starts Confirmed, or Rescheduled when it replaces one cancelled in
 *   the same request.
 * - **Off-shift ADD** leaves out `coach_shift_id` and sends the typed
 *   `slot_start`/`slot_end`. The API accepts that from coaches, managers and
 *   executives.
 * - **UPDATE** changes a booking by its `id`: `booking_status_id` (with an
 *   optional `status_reason`, written to the status history) and/or
 *   `feedback`/`rating`. `user_id`, `coach_id` and the times are immutable —
 *   sending any of them is a 400. A booking may appear only once per request.
 *
 * Every status change appends a `BookingStatusHistory` row; nothing is
 * overwritten. A reschedule is an UPDATE to Cancelled plus an ADD, sent
 * together (UPDATEs are applied first).
 *
 * @param {Function} post - The `post` verb from ApiContext.
 * @param {Object} params
 * @param {string} params.eventTypeId
 * @param {Array<{ operation: 'ADD'|'UPDATE', id?: string, user_id?: string, coach_id?: string,
 *   slot_start?: string, booking_status_id?: string, status_reason?: string,
 *   feedback?: string, rating?: number }>} params.bookings
 * @returns {Promise<{ booking_summary?: Object, booking_records?: Array<Object> }>}
 */
export const modifyBookings = async (post, { eventTypeId, bookings } = {}) => {
  try {
    const response = await post(
      "/modify-booking",
      { event_type_id: eventTypeId, bookings },
      { instance: axiosSubscriptionInstance }
    );
    // `post` resolves to { data, status } around { message, data }.
    return response?.data?.data || response?.data || {};
  } catch (err) {
    throw wrapCommsError(err, "Failed to update this booking");
  }
};
